import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, User, LogOut, Newspaper, MapPin, Receipt, Sun, Moon } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../lib/auth";
import { useSystemSettings } from "../lib/systemSettings";
import { api } from "../lib/api";
import PortalPopups from "./PortalPopups";
import VerificationBanner from "./VerificationBanner";

// NAV wird zur Laufzeit im Component aufgebaut. Reihenfolge folgt der
// UAT-Vorgabe: Übersicht → News → Standorte → Einkäufe → Mein Konto.
// „Loop Club" (früher eigener Tab) wurde entfernt — der QR-Code lebt jetzt
// im Übersicht-Modal beim Feld „Club Kartennummer".
function buildNav() {
  return [
    { to: "/portal", label: "Übersicht", icon: LayoutDashboard, testId: "portal-nav-home", end: true },
    { to: "/portal/news", label: "News & Angebote", icon: Newspaper, testId: "portal-nav-news" },
    { to: "/portal/locations", label: "Standorte", icon: MapPin, testId: "portal-nav-locations" },
    { to: "/portal/purchases", label: "Meine Einkäufe", icon: Receipt, testId: "portal-nav-purchases" },
    { to: "/portal/account", label: "Mein Konto", icon: User, testId: "portal-nav-account" },
  ];
}

/**
 * Portal-Rahmen inkl. Kunden-Theme-Toggle (Sun/Moon).
 *
 * Kunden-Theme-Präferenz wird zusätzlich zum LocalStorage (sofortiges
 * Anwenden) auch server-seitig auf ``customer_accounts.theme_preference``
 * persistiert, damit die Auswahl über Geräte hinweg gilt. Der Toggle
 * verändert das globale Admin-Default-Theme NICHT.
 */
export default function PortalLayout() {
  const { user, logout } = useAuth();
  const { settings, theme, setTheme, brandName } = useSystemSettings();
  const logoUrl = settings?.logo_data_url || null;
  const NAV = buildNav();

  const effectiveDark = theme !== "light";

  async function toggleTheme() {
    const next = effectiveDark ? "light" : "dark";
    setTheme(next);
    // Best-effort server-persist. Fehler still ignorieren (LocalStorage
    // reicht als Fallback und wurde soeben schon gesetzt).
    try { await api.patch("/customer/me", { theme_preference: next }); } catch {}
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" data-testid="portal-root">
      <div className="border-b border-zinc-800 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt="" data-testid="portal-logo" className="h-10 w-auto max-w-[180px] object-contain" />
            ) : (
              <div className="min-w-0">
                <div data-testid="portal-logo-fallback"
                     className="font-black tracking-tight text-cyan-400 uppercase truncate text-xl sm:text-2xl leading-tight">
                  {brandName}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-500">Kundenportal</div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              data-testid="portal-theme-toggle"
              onClick={toggleTheme}
              title={effectiveDark ? "Zum hellen Design wechseln" : "Zum dunklen Design wechseln"}
              className="w-9 h-9 rounded-full border border-zinc-800 hover:border-cyan-400 text-zinc-400 hover:text-cyan-400 flex items-center justify-center transition-colors"
            >
              {effectiveDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="hidden sm:block text-sm text-zinc-400 truncate max-w-[160px]">
              {user?.name || user?.email}
            </div>
            <Button data-testid="portal-logout-btn" size="sm" variant="outline" onClick={logout} className="border-zinc-700">
              <LogOut size={14} className="mr-1" /> Abmelden
            </Button>
          </div>
        </div>
        <nav className="max-w-4xl mx-auto px-2 sm:px-4 flex gap-1 overflow-x-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to} to={item.to} end={item.end}
              data-testid={item.testId}
              className={({ isActive }) =>
                `px-3 sm:px-4 py-2 text-xs sm:text-sm font-mono uppercase tracking-wider transition-colors whitespace-nowrap ${
                  isActive
                    ? "border-b-2 border-cyan-400 text-cyan-400"
                    : "border-b-2 border-transparent text-zinc-400 hover:text-zinc-200"
                }`
              }
            >
              <span className="inline-flex items-center gap-2">
                <item.icon size={14} /> {item.label}
              </span>
            </NavLink>
          ))}
        </nav>
      </div>
      <main className="max-w-4xl w-full mx-auto p-4 sm:p-6 flex-1">
        <VerificationBanner />
        <Outlet />
      </main>

      <PortalPopups />

      <footer className="border-t border-zinc-800 py-4 text-center text-[11px] tracking-[0.2em] uppercase text-zinc-500 space-y-2" data-testid="portal-footer">
        <div className="flex justify-center gap-4 sm:gap-6 flex-wrap">
          <NavLink to="/portal/impressum" data-testid="portal-footer-impressum" className="hover:text-cyan-400 py-1 px-1">Impressum</NavLink>
          <span className="text-zinc-700">·</span>
          <NavLink to="/portal/datenschutz" data-testid="portal-footer-datenschutz" className="hover:text-cyan-400 py-1 px-1">Datenschutz</NavLink>
          <span className="text-zinc-700">·</span>
          <NavLink to="/portal/agb" data-testid="portal-footer-agb" className="hover:text-cyan-400 py-1 px-1">AGB</NavLink>
        </div>
        <div className="text-zinc-600 uppercase">{brandName ? `${brandName} © 2026` : "© 2026"}</div>
      </footer>
    </div>
  );
}
